/**
 * UnifiedTopNav — shared cross-shell navigation ribbon (Iter I-20 · Track D).
 *
 * Fully responsive:
 *  - Desktop (≥lg / 1024px): horizontal nav ribbon (as before)
 *  - Mobile / Tablet (<1024px): compact top bar with hamburger button that
 *    opens an off-canvas drawer containing the same navigation items.
 */
import { useEffect, useState } from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import {
  Home, FolderKanban, Settings, HelpCircle, LogOut, User, Menu, X, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import ReportsDrawer from "@/components/reports/ReportsDrawer";

const NAV_ITEMS = [
  { key: "home",     label: "Home",     to: "/dashboard",           icon: Home,          testid: "nav-home" },
  { key: "projects", label: "Projects", to: "/projects",            icon: FolderKanban,  testid: "nav-projects" },
];

const ADMIN_ITEMS = [
  { key: "settings", label: "Settings", to: "/company",             icon: Settings,      testid: "nav-settings" },
];

export default function UnifiedTopNav({ workspaceToggle, compact = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = user?.platform_role === "super_admin";
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close drawer on route change.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Esc to close + lock body scroll while drawer is open.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => e.key === "Escape" && setDrawerOpen(false);
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  const linkCls = ({ isActive }) =>
    "px-2.5 h-8 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors " +
    (isActive
      ? "text-[#4CAF50] border-b-2 border-[#4CAF50]"
      : "text-neutral-600 hover:text-neutral-900");

  // Drawer item — larger tap-targets, vertical layout.
  const drawerLinkCls = ({ isActive }) =>
    "flex items-center gap-3 px-4 h-12 font-medium text-[14px] tracking-tight transition-colors border-l-4 " +
    (isActive
      ? "text-[#4CAF50] border-[#4CAF50] bg-[#4CAF50]/5"
      : "text-neutral-700 border-transparent hover:bg-neutral-50 hover:text-neutral-900");

  const help = () => {
    const url = process.env.REACT_APP_DOCS_URL || "";
    if (url) window.open(url, "_blank", "noopener");
    else navigate("/dashboard");
  };

  return (
    <>
      <nav
        data-testid="unified-top-nav"
        className="h-12 lg:h-9 border-b border-neutral-200 bg-white flex items-center px-3 gap-1"
      >
        {/* MOBILE / TABLET: hamburger + brand + push actions to the right */}
        <div className="flex lg:hidden items-center gap-2 flex-1 min-w-0">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="topnav-hamburger"
            aria-label="Open menu"
            className="h-10 w-10 grid place-items-center rounded-md hover:bg-neutral-100 transition-colors"
          >
            <Menu className="h-5 w-5" strokeWidth={2} />
          </button>
          <Link
            to="/dashboard"
            data-testid="topnav-brand"
            className="min-w-0 flex items-center"
            title="SMIN"
          >
            <img
              src="/branding/smin-logo-horizontal.png?v=9"
              alt="SMIN"
              draggable={false}
              className="h-7 w-auto object-contain"
            />
          </Link>
        </div>

        {/* DESKTOP: full horizontal ribbon */}
        <div className="hidden lg:flex items-center gap-1 flex-1 overflow-x-auto">
          {NAV_ITEMS.map((it) => (
            <NavLink
              key={it.key} to={it.to} end={it.to === "/dashboard"}
              className={linkCls}
              data-testid={it.testid}
              title={it.hint || it.label}
            >
              <it.icon className="h-3.5 w-3.5 shrink-0" />
              {!compact && <span>{it.label}</span>}
            </NavLink>
          ))}
          <div className="flex-1" />
          {isSuperAdmin && (
            <NavLink to="/admin/signup-requests" className={linkCls} data-testid="nav-admin"
              title="Signup review queue">
              <Settings className="h-3.5 w-3.5" />
              {!compact && <span>Admin</span>}
            </NavLink>
          )}
          {ADMIN_ITEMS.map((it) => (
            <NavLink key={it.key} to={it.to} className={linkCls}
              data-testid={it.testid} title={it.label}>
              <it.icon className="h-3.5 w-3.5" />
              {!compact && <span>{it.label}</span>}
            </NavLink>
          ))}
          <button
            onClick={help}
            className="px-2.5 h-8 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-neutral-400 hover:text-neutral-100 transition-colors"
            data-testid="nav-help">
            <HelpCircle className="h-3.5 w-3.5" />
            {!compact && <span>Help</span>}
          </button>
          {workspaceToggle && (
            <div className="pl-2 ml-1 border-l border-neutral-200">
              {workspaceToggle}
            </div>
          )}
        </div>

        {/* User + Sign out — always on the right */}
        {user && (
          <div className="pl-2 lg:pl-3 lg:ml-2 lg:border-l border-neutral-200 flex items-center gap-2 shrink-0">
            <ReportsDrawer />
            <Link
              to="/profile"
              data-testid="topbar-user"
              className="hidden sm:flex items-center gap-2 h-9 lg:h-8 px-2 rounded-md text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
              title="Edit profile"
            >
              <User className="h-4 w-4 lg:h-3.5 lg:w-3.5" />
              <span className="font-mono text-[11px] uppercase tracking-widest hidden md:inline">
                {user?.name || user?.email || "Account"}
              </span>
            </Link>
            <button
              type="button"
              onClick={() => { logout?.(); navigate("/"); }}
              data-testid="nav-signout"
              className="inline-flex items-center gap-1.5 h-9 lg:h-8 px-3 rounded-md font-semibold text-[11px] uppercase tracking-widest text-white transition-colors shrink-0"
              style={{ background: "#4CAF50" }}
              onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
              title="Sign out"
            >
              <LogOut className="h-4 w-4 lg:h-3.5 lg:w-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        )}
      </nav>

      {/* Off-canvas mobile / tablet drawer. */}
      {drawerOpen && (
        <div
          className="lg:hidden"
          data-testid="topnav-drawer-overlay"
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="smin-drawer-backdrop"
            data-testid="topnav-drawer-backdrop"
            aria-hidden="true"
          />
          <aside
            className="smin-drawer-panel"
            data-testid="topnav-drawer"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 h-14 border-b border-neutral-200">
              <img
                src="/branding/smin-logo-horizontal.png?v=9"
                alt="SMIN"
                draggable={false}
                className="h-8 w-auto object-contain"
              />
              <button
                onClick={() => setDrawerOpen(false)}
                data-testid="topnav-drawer-close"
                aria-label="Close menu"
                className="h-10 w-10 grid place-items-center rounded-md hover:bg-neutral-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="py-3 flex flex-col">
              {NAV_ITEMS.map((it) => (
                <NavLink
                  key={it.key}
                  to={it.to}
                  end={it.to === "/dashboard"}
                  className={drawerLinkCls}
                  data-testid={`drawer-${it.testid}`}
                >
                  <it.icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                  <span>{it.label}</span>
                </NavLink>
              ))}

              {isSuperAdmin && (
                <NavLink
                  to="/admin/users"
                  className={drawerLinkCls}
                  data-testid="drawer-nav-admin"
                >
                  <ShieldCheck className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                  <span>Admin</span>
                </NavLink>
              )}

              {ADMIN_ITEMS.map((it) => (
                <NavLink
                  key={it.key}
                  to={it.to}
                  className={drawerLinkCls}
                  data-testid={`drawer-${it.testid}`}
                >
                  <it.icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                  <span>{it.label}</span>
                </NavLink>
              ))}

              <div className="border-t border-neutral-200 mt-2" />

              <Link
                to="/profile"
                className={drawerLinkCls({ isActive: false })}
                data-testid="nav-profile"
              >
                <User className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                <span>Profile</span>
              </Link>

              <button
                onClick={help}
                data-testid="drawer-nav-help"
                className="flex items-center gap-3 px-4 h-12 font-medium text-[14px] tracking-tight text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 border-l-4 border-transparent text-left"
              >
                <HelpCircle className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                <span>Help</span>
              </button>
            </div>

            <div className="mt-auto p-4 border-t border-neutral-200">
              <button
                onClick={() => { logout?.(); navigate("/"); }}
                data-testid="drawer-signout"
                className="w-full h-11 rounded-md font-semibold text-sm text-white inline-flex items-center justify-center gap-2 transition-colors"
                style={{ background: "#4CAF50" }}
              >
                <LogOut className="h-4 w-4" />
                <span>Sign out</span>
              </button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
